import { Button, Card } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Pagination } from "../types/form";
import { CustomHeaderItem } from "../types/header";
import { getImageUrl } from "../utils";
import { getAllCustomHeaders, getAllGlobalHeaders } from "../utils/requests";

interface HeaderThemeSelectProps {
	onSelectHeader: (header: CustomHeaderItem) => void;
}

const AllHeaderSelectModal: React.FC<HeaderThemeSelectProps> = ({
	onSelectHeader,
}) => {
	const [headers, setHeaders] = useState<CustomHeaderItem[]>([]);
	const [pagination, setPagination] = useState<Pagination | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [selectedHeader, setSelectedHeader] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const [type, setType] = useState<"local" | "global">("local");

	useEffect(() => {
		fetchHeaders(1, 10, type);
	}, [type]);

	const fetchHeaders = async (
		page = 1,
		perPage = 10,
		type: "local" | "global" = "local"
	) => {
		try {
			setLoading(true);
			const response = await (type === "global"
				? getAllGlobalHeaders
				: getAllCustomHeaders)({
				perPage,
				page,
			});

			setHeaders(response.data);
			setPagination(response.page);
			setError(null);
		} catch (err) {
			setError("Error fetching headers. Please try again.");
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectHeader = (header: CustomHeaderItem) => {
		setSelectedHeader(header._id);
		onSelectHeader(header);
	};

	const renderHeaderPreview = (header: CustomHeaderItem) => {
		if (header.images) {
			return (
				<div className="h-32 w-full overflow-hidden rounded-t-md">
					<img
						src={header.images}
						alt={header.name}
						className="w-full h-full object-cover"
					/>
				</div>
			);
		} else if (header.settings?.content) {
			return (
				<div
					className="h-80 w-full overflow-hidden rounded-t-md bg-gray-100"
					style={{
						zoom: 0.3,
					}}
					dangerouslySetInnerHTML={{
						__html: header.settings.content,
					}}
				/>
			);
		} else {
			return (
				<div className="h-32 w-full bg-gray-200 rounded-t-md flex items-center justify-center text-gray-500">
					No preview available
				</div>
			);
		}
	};

	if (loading && headers.length === 0) {
		return (
			<div className="flex justify-center p-8">Loading headers...</div>
		);
	}

	if (error) {
		return <div className="text-red-500 p-4">{error}</div>;
	}

	return (
		<div className="container mx-auto p-4 min-w-[600px] max-h-[70vh] overflow-y-auto">
			<h2 className="text-xl font-bold mb-6">Select Header Theme</h2>

			<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
				{/* select type */}
				<div className="col-span-3 flex justify-end gap-4 mb-4">
					<Button
						variant={type === "local" ? "contained" : "outlined"}
						className={`mr-2 ${
							type === "local" ? "bg-blue-500 text-white" : ""
						}`}
						onClick={() => setType("local")}>
						Local Headers
					</Button>
					<Button
						variant={type === "global" ? "contained" : "outlined"}
						className={`${
							type === "global" ? "bg-blue-500 text-white" : ""
						}`}
						onClick={() => setType("global")}>
						Global Headers
					</Button>
				</div>

				{headers.map((header) => (
					<Card
						key={header._id}
						className={`cursor-pointer transition-all duration-200 hover:shadow-md overflow-hidden ${
							selectedHeader === header._id
								? "ring-2 ring-blue-500"
								: ""
						}`}
						onClick={() => handleSelectHeader(header)}>
						{header.images ? (
							<img
								src={getImageUrl(header.images)}
								alt={header.name}
								className="w-full h-72 object-cover"
							/>
						) : (
							renderHeaderPreview(header)
						)}

						<div className="p-4">
							<div className="flex justify-between items-start">
								<div>
									<h3 className="font-semibold truncate">
										{header.name}
									</h3>
									<p className="text-sm text-gray-600 mt-1 line-clamp-2">
										{header.description || "No description"}
									</p>
									<div className="flex mt-2 space-x-2">
										<span className="text-xs bg-gray-100 px-2 py-1 rounded">
											{header.screenMode}
										</span>
										{header.isFrontHeader && (
											<span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
												Front
											</span>
										)}
									</div>
								</div>
							</div>
						</div>
					</Card>
				))}
			</div>

			{headers.length === 0 && !loading && (
				<div className="text-center py-8">
					<p className="text-gray-500">No header themes found</p>
				</div>
			)}

			{pagination && pagination.totalPage > 1 && (
				<div className="flex justify-center mt-6 space-x-2">
					<Button
						variant="outlined"
						disabled={pagination.currentPage <= 1}
						onClick={() =>
							fetchHeaders(pagination.currentPage - 1, 10, type)
						}>
						Previous
					</Button>

					<span className="py-2 px-4">
						Page {pagination.currentPage} of {pagination.totalPage}
					</span>

					<Button
						variant="outlined"
						disabled={
							pagination.currentPage >= pagination.totalPage
						}
						onClick={() =>
							fetchHeaders(pagination.currentPage + 1, 10, type)
						}>
						Next
					</Button>
				</div>
			)}
		</div>
	);
};

export default AllHeaderSelectModal;
